import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import { CenterPanel } from "@/components/layout/center-panel";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useCompanies } from "@/hooks/use-companies";
import { companiesApi, type Company } from "@/api/companies";
import { queryKeys } from "@/lib/query-keys";
import {
  TenantLogo,
  TenantStatusBadge,
  TenantCounts,
  useAllTenantAgents,
  useOpenTickets,
} from "@/components/admin/tenant-summary";
import { Building2, Plus, ArrowRight, Settings, Upload, X, Trash2, AlertTriangle, Search } from "lucide-react";

function LogoUploadDialog({
  company,
  onClose,
}: {
  company: Company;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);
  const [preview, setPreview] = useState<string | null>(company.logoUrl);

  const updateLogo = useMutation({
    mutationFn: (logoUrl: string | null) =>
      companiesApi.update(company.id, { logoUrl }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.companies.all });
      onClose();
    },
  });

  const handleFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 150_000) {
      alert("Logo must be under 150KB");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setPreview(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div className="w-[min(400px,calc(100vw-2rem))] rounded-xl border border-border bg-surface p-6" onClick={(e) => e.stopPropagation()}>
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-text-primary">
            Logo for {company.name}
          </h3>
          <button onClick={onClose} className="text-text-muted hover:text-text-primary">
            <X size={16} />
          </button>
        </div>

        <div className="mb-4 flex flex-col items-center gap-3">
          {preview ? (
            <img src={preview} alt="Preview" className="h-20 w-20 rounded-lg object-contain border border-border" />
          ) : (
            <div className="flex h-20 w-20 items-center justify-center rounded-lg border border-dashed border-border">
              <Building2 size={32} className="text-text-muted" />
            </div>
          )}
          <input
            ref={fileRef}
            type="file"
            accept="image/png,image/jpeg,image/svg+xml,image/webp"
            className="hidden"
            onChange={handleFile}
          />
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => fileRef.current?.click()}>
              <Upload size={14} className="mr-1.5" />
              Choose File
            </Button>
            {preview && (
              <Button size="sm" variant="outline" onClick={() => setPreview(null)}>
                Remove
              </Button>
            )}
          </div>
          <p className="text-[11px] text-text-muted">PNG, JPG, SVG or WebP. Max 150KB.</p>
        </div>

        <div className="flex justify-end gap-2">
          <Button size="sm" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            size="sm"
            disabled={updateLogo.isPending}
            onClick={() => updateLogo.mutate(preview)}
          >
            {updateLogo.isPending ? "Saving..." : "Save Logo"}
          </Button>
        </div>
      </div>
    </div>
  );
}

function RemoveTenantDialog({
  company,
  onClose,
  onRemoved,
}: {
  company: Company;
  onClose: () => void;
  onRemoved: () => void;
}) {
  const [confirmText, setConfirmText] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  const { data: inventory, isLoading: inventoryLoading } = useQuery({
    queryKey: ["removal-inventory", company.id],
    queryFn: () => companiesApi.removalInventory(company.id),
  });

  const removeMutation = useMutation({
    mutationFn: () => companiesApi.delete(company.id, confirmText),
    onSuccess: () => onRemoved(),
  });

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const isMatch = confirmText === company.name;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      onClick={onClose}
    >
      <div
        className="w-[min(520px,calc(100vw-2rem))] rounded-xl border border-red-500/30 bg-surface p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-red-500/10">
            <AlertTriangle size={20} className="text-red-500" />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-red-500">
              Remove Tenant Permanently
            </h3>
            <p className="text-xs text-text-muted">
              This action is irreversible
            </p>
          </div>
        </div>

        <div className="mb-4 rounded-lg border border-red-500/20 bg-red-500/5 p-3 text-xs text-text-secondary leading-relaxed">
          <p className="font-semibold text-red-400 mb-2">
            WARNING: This will permanently destroy:
          </p>
          <ul className="list-disc pl-4 space-y-1">
            <li>
              All agents and their configurations
              {inventory && inventory.agents > 0 && (
                <span className="text-text-muted">
                  {" "}({inventory.agents}: {inventory.agentNames.join(", ")})
                </span>
              )}
            </li>
            <li>
              All Azure VMs and resource groups
              {inventory && inventory.azureResourceGroups.length > 0 && (
                <span className="text-text-muted">
                  {" "}({inventory.azureResourceGroups.join(", ")})
                </span>
              )}
            </li>
            <li>All chat history, files, and conversation data</li>
            <li>All tenant users, groups, and access configurations</li>
            <li>
              All deployed apps and app service plans
              {inventory && inventory.tenantApps > 0 && (
                <span className="text-text-muted"> ({inventory.tenantApps} apps)</span>
              )}
            </li>
            <li>VNet peering and network configurations</li>
          </ul>
          <p className="mt-2 font-semibold text-red-400">
            None of this data is backed up. There is no undo.
          </p>
        </div>

        {inventoryLoading && (
          <p className="mb-3 text-xs text-text-muted">Loading resource inventory...</p>
        )}

        <div className="mb-4">
          <label className="mb-1.5 block text-xs text-text-secondary">
            Type <span className="font-mono font-semibold text-text-primary">{company.name}</span> to confirm:
          </label>
          <input
            ref={inputRef}
            type="text"
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder={company.name}
            className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-text-primary placeholder:text-text-muted/40 focus:border-red-500 focus:outline-none focus:ring-1 focus:ring-red-500"
            onKeyDown={(e) => {
              if (e.key === "Enter" && isMatch && !removeMutation.isPending) {
                removeMutation.mutate();
              }
            }}
          />
        </div>

        {removeMutation.isError && (
          <p className="mb-3 text-xs text-red-500">
            Removal failed: {removeMutation.error instanceof Error ? removeMutation.error.message : "Unknown error"}
          </p>
        )}

        <div className="flex justify-end gap-2">
          <Button size="sm" variant="outline" onClick={onClose} disabled={removeMutation.isPending}>
            Cancel
          </Button>
          <Button
            size="sm"
            disabled={!isMatch || removeMutation.isPending}
            onClick={() => removeMutation.mutate()}
            className="bg-red-600 text-white hover:bg-red-700 disabled:bg-red-600/40"
          >
            {removeMutation.isPending ? "Removing..." : "Permanently Remove Tenant"}
          </Button>
        </div>
      </div>
    </div>
  );
}

export function Tenants() {
  const navigate = useNavigate();
  const { data: companies = [], isLoading } = useCompanies();
  const queryClient = useQueryClient();
  const [logoTarget, setLogoTarget] = useState<Company | null>(null);
  const [removeTarget, setRemoveTarget] = useState<Company | null>(null);
  const [search, setSearch] = useState("");

  const { data: allAgents = [] } = useAllTenantAgents(companies);
  const openTickets = useOpenTickets();

  const query = search.trim().toLowerCase();
  const shown = query
    ? companies.filter((c) => c.name.toLowerCase().includes(query))
    : companies;

  return (
    <CenterPanel title="Tenants">
      <div className="mb-4 flex items-center gap-3">
        <div className="relative w-64">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" />
          <input
            type="text"
            placeholder="Search tenants..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full rounded-md border border-border bg-background py-1.5 pl-9 pr-3 text-sm placeholder:text-text-muted focus:outline-none focus:ring-1 focus:ring-border"
          />
        </div>
        <p className="ml-auto text-sm text-text-secondary">
          {query ? `${shown.length} of ${companies.length}` : companies.length} tenant
          {companies.length !== 1 ? "s" : ""}
        </p>
        <Button size="sm" onClick={() => navigate("/tenants/new")}>
          <Plus size={16} className="mr-1.5" />
          New Tenant
        </Button>
      </div>

      {isLoading ? (
        <p className="text-sm text-text-muted">Loading tenants...</p>
      ) : companies.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12 text-center">
            <Building2 size={40} className="mb-3 text-text-muted" />
            <p className="text-sm text-text-secondary">No tenants yet. Create one to get started.</p>
          </CardContent>
        </Card>
      ) : shown.length === 0 ? (
        <p className="py-6 text-center text-sm text-text-muted">No tenants match "{search.trim()}".</p>
      ) : (
        <div className="flex flex-col gap-2">
          {shown.map((company) => {
            const agentCount = allAgents.filter((a) => a.companyId === company.id).length;
            const ticketCount = openTickets.filter((t) => t.companyId === company.id).length;
            return (
            <Card
              key={company.id}
              className="cursor-pointer transition-colors hover:border-accent/40"
              onClick={() => navigate(`/tenants/${company.id}/dashboard`)}
            >
              <CardContent className="flex items-center justify-between py-4">
                <div className="flex items-center gap-3">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setLogoTarget(company);
                    }}
                    className="group relative"
                    title="Change logo"
                  >
                    <TenantLogo company={company} />
                    <div className="absolute inset-0 flex items-center justify-center rounded-lg bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity">
                      <Upload size={14} className="text-white" />
                    </div>
                  </button>
                  <div>
                    <p className="text-sm font-medium text-text-primary">{company.name}</p>
                    <TenantCounts agents={agentCount} openTickets={ticketCount} />
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <TenantStatusBadge status={company.status} />
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/tenants/${company.id}/admin/models`);
                    }}
                    title="Model Settings"
                  >
                    <Settings size={14} className="mr-1" />
                    Model Settings
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={(e) => {
                      e.stopPropagation();
                      setRemoveTarget(company);
                    }}
                    title="Remove tenant"
                    className="border-red-500/30 text-red-500 hover:bg-red-500/10 hover:text-red-400"
                  >
                    <Trash2 size={14} />
                  </Button>
                  <ArrowRight size={16} className="text-text-muted" />
                </div>
              </CardContent>
            </Card>
            );
          })}
        </div>
      )}


      {logoTarget && (
        <LogoUploadDialog
          company={logoTarget}
          onClose={() => setLogoTarget(null)}
        />
      )}

      {removeTarget && (
        <RemoveTenantDialog
          company={removeTarget}
          onClose={() => setRemoveTarget(null)}
          onRemoved={() => {
            setRemoveTarget(null);
            queryClient.invalidateQueries({ queryKey: queryKeys.companies.all });
          }}
        />
      )}
    </CenterPanel>
  );
}
